<script setup lang="ts">
import { computed } from 'vue'

import { useSetupLabels } from '../composables/useSetupLabels'
import type { AssistantSend, ChatMessage } from '../models/orpheus.types'
import type { ClarificationOption } from '../models/orpheusClarification.types'
import { clarificationFilter } from '../service/orpheusClarification'
import { previewSubtitle } from '../service/orpheusFilePreviewPresentation'
import { hasSetupDraft } from '../service/orpheusSetupCardPresentation'
import {
  createRecommendationDecisionIntent,
  type RecommendationDecisionIntent,
} from '../service/recommendationDecision'

import InlineReportStrip from './InlineReportStrip.vue'
import OrpheusClarification from './OrpheusClarification.vue'
import OrpheusKeyErrorCard from './key/OrpheusKeyErrorCard.vue'
import RecommendationCard from './RecommendationCard.vue'
import OrpheusSetupCard from './setup/OrpheusSetupCard.vue'
import OrpheusSetupQuestion from './setup/OrpheusSetupQuestion.vue'
import StreamingMessage from './StreamingMessage.vue'

import { useCreditGuard } from '@/features/credits'
import { useI18n } from '@/shared/i18n'
import { Button, Icon } from '@/shared/ui'
import { RichTextViewer } from '@/shared/ui/editor'

const props = withDefaults(
  defineProps<{
    message: ChatMessage
    activeAnswer?: boolean
    reportPreview?: boolean
    busy?: boolean
    /** The thread's newest settled answer — the only one whose setup question takes an answer. */
    latest?: boolean
    /** The thread's newest confirm card — a newer preview supersedes every earlier draft. */
    latestCard?: boolean
    /** Setup options that can't be picked, by asking field, then by value, with the reason. */
    unavailableOptions?: Record<string, Record<string, string>>
  }>(),
  {
    activeAnswer: false,
    reportPreview: true,
    busy: false,
    latest: false,
    latestCard: false,
    unavailableOptions: () => ({}),
  },
)
const emit = defineEmits<{
  cancel: []
  retry: []
  send: [payload: AssistantSend]
  'answer-with-credits': []
  'open-key-settings': []
  'open-answer': []
  'open-file-preview': []
  'confirm-action': [intent: RecommendationDecisionIntent]
}>()
const { t } = useI18n()
const creditGuard = useCreditGuard()
const labels = useSetupLabels()

/**
 * 403 PACKAGE_FORBIDDEN only. The copy promises an upgrade ("…Upgrade to continue."), and unlike
 * the 402 path nothing else on the page offers one: `FeatureDepletedBanner` and `OrpheusCreditHint`
 * both key off `creditStore.isLocked`, which a plan rejection deliberately leaves untouched
 * (contract 11 §9). 402 already renders two Upgrade buttons, so it is excluded here.
 */
const planUpgradable = computed(() => props.message.errorKey === 'orpheus.error.packageForbidden')

const failed = computed(
  () =>
    !props.message.keyError &&
    (props.message.kind === 'error' ||
      props.message.streamState === 'reconnectable-error' ||
      props.message.streamState === 'terminal-error'),
)
const showLifecycle = computed(
  () => props.message.streamState !== 'idle' && props.message.streamState !== 'completed',
)
// A clarification's question is its own title (often the answer's text itself), so the text
// would read twice (v3 hid it the same way).
const showContent = computed(() => Boolean(props.message.content) && !props.message.clarification)
const setupStep = computed(() => props.message.setupStep ?? null)
const showSetupCard = computed(() =>
  hasSetupDraft(props.message.setupCard ?? null, setupStep.value?.slots ?? []),
)
const filePreviewSubtitle = computed(() =>
  props.message.filePreview ? previewSubtitle(labels, props.message.filePreview) : '',
)

function confirmRecommendation(index: number, accepted: boolean): void {
  const recommendation = props.message.recommendations[index]
  if (recommendation) {
    emit('confirm-action', createRecommendationDecisionIntent(recommendation, accepted))
  }
}

function selectClarification(option: ClarificationOption): void {
  const selectedFilter = clarificationFilter(option)
  emit('send', { text: option.message, ...(selectedFilter ? { selectedFilter } : {}) })
}
</script>

<template>
  <div class="assistant-message grid gap-xs min-w-0" :data-stream-state="message.streamState">
    <OrpheusKeyErrorCard
      v-if="message.keyError"
      :key-error="message.keyError"
      :loading="busy"
      @answer-with-credits="emit('answer-with-credits')"
      @try-again="emit('retry')"
      @open-settings="emit('open-key-settings')"
    />
    <RichTextViewer v-else-if="showContent" :content="message.content" />
    <div
      v-if="failed"
      class="assistant-message__error flex flex-wrap items-center gap-sm m-0 text-danger text-[0.875rem]"
      role="alert"
    >
      <p>{{ t(message.errorKey ?? 'orpheus.error.generic') }}</p>
      <Button
        v-if="planUpgradable"
        class="assistant-message__upgrade"
        variant="primary"
        size="sm"
        @click="creditGuard.openUpgrade"
      >
        {{ t('credits.panel.upgrade') }}
      </Button>
    </div>
    <RecommendationCard
      v-for="(recommendation, index) in message.recommendations"
      :key="recommendation.id ?? `${recommendation.entity}-${recommendation.category}-${index}`"
      :recommendation="recommendation"
      :busy="busy"
      @confirm="confirmRecommendation(index, $event)"
    />
    <StreamingMessage v-if="showLifecycle" :message="message" @cancel="emit('cancel')" />
    <InlineReportStrip
      v-if="message.answer"
      :message="message"
      :active="activeAnswer"
      :preview="reportPreview"
      @open-answer="emit('open-answer')"
    />

    <OrpheusClarification
      v-if="message.clarification"
      :clarification="message.clarification"
      :loading="busy"
      @select="selectClarification"
    />

    <!-- A setup turn shows its draft so far: the confirm card once there is one, otherwise the
         fields filled up to this question, once at least one is (KUR-8). -->
    <OrpheusSetupCard
      v-if="showSetupCard"
      :card="message.setupCard ?? null"
      :slots="setupStep?.slots ?? []"
      :active="latestCard"
      :loading="busy"
      @send="emit('send', { text: $event })"
    />

    <button
      v-if="message.filePreview"
      type="button"
      class="flex w-full cursor-pointer items-center justify-between gap-3 rounded-xl border border-orpheus-brand/15 bg-orpheus-brand/5 px-4 py-3 text-left shadow-sm transition-all duration-200 hover:border-orpheus-brand/35 hover:bg-orpheus-brand/10"
      data-testid="orpheus-file-preview-strip"
      @click="emit('open-file-preview')"
    >
      <span class="min-w-0">
        <span class="block truncate text-sm font-semibold text-primary">
          {{ t('orpheus.filePreview.strip') }}
        </span>
        <span class="block truncate text-xs text-muted">{{ filePreviewSubtitle }}</span>
      </span>
      <Icon name="chevron-right" :size="14" weight="light" class="shrink-0 text-subtle" />
    </button>

    <OrpheusSetupQuestion
      v-if="setupStep?.options.length"
      :step="setupStep"
      :show-question="!message.content"
      :interactive="latest"
      :loading="busy"
      :unavailable="unavailableOptions[setupStep.askingField] ?? {}"
      @answer="emit('send', { text: $event })"
    />
  </div>
</template>

<style scoped>
.assistant-message .assistant-message__error p {
  margin: 0;
}
</style>
